import * as React from 'react';
import { Box, Button, Stack, Typography, useTheme } from '@rabex-kit/rabex-ui';
import { keyframes } from '@mui/material/styles';
import { useOnlineStatus } from '@/pwa/hooks/useOnlineStatus';
import SplashBackdrop from '@/components/splash/SplashBackdrop';
import AnimatedRabexMark from '@/components/splash/AnimatedRabexMark';
import { getSplashColors } from '@/components/splash/splashColors';

const fadeIn = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`;

/**
 * Full-screen overlay shown whenever navigator.onLine === false.
 *
 * Pairs with the SW's offline.html:
 *   - offline.html    : COLD navigation while offline (no SPA shell yet
 *                       OR precache evicted) — see notes in sw.js
 *   - OfflineOverlay  : WARM offline (user already inside the SPA)
 *
 * In practice the user almost always hits this one — once the SW is
 * installed, the precached index.html survives offline navigations and
 * the SPA renders this overlay. offline.html only fires if the precache
 * itself is also unavailable.
 *
 * Visual: same splash language as preboot + RouteFallback so an offline
 * state feels like the app politely waiting, not a system error.
 * Colours come from splashColors (not palette tokens) to stay identical
 * with the static splash CSS, in light and dark mode.
 */
export default function OfflineOverlay() {
  const isOnline = useOnlineStatus();
  const { palette } = useTheme();

  if (isOnline) return null;

  return (
    <Box
      role="alertdialog"
      aria-live="assertive"
      aria-label="ارتباط با سرور"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 100000, // above install nudges and update banner
        bgcolor: getSplashColors(palette.mode).bg,
        overflow: 'hidden',
        animation: `${fadeIn} 200ms ease-out`,
        display: 'flex',
        flexDirection: 'column'
      }}>
      <SplashBackdrop />

      {/* Centered logo + copy */}
      <Stack
        sx={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
          px: 3
        }}>
        <AnimatedRabexMark size={60} />

        <Stack alignItems="center" gap={1}>
          <Typography variant="body1" weight="semiBold">
            ارتباط با سرور
          </Typography>
          <Typography
            variant="body2"
            color={palette.textColor[400]}
            sx={{ textAlign: 'center', maxWidth: 359 }}>
            ارتباط با سرور برقرار نشد. پس از کسب اطمینان از اتصال گوشی‌تان به
            اینترنت، مجدداً تلاش کنید.
          </Typography>
        </Stack>
      </Stack>

      {/* Bottom CTA — safe-area inset for devices with home indicators */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          px: 3,
          pt: 2,
          pb: 'max(24px, env(safe-area-inset-bottom))'
        }}>
        <Button
          variant="contained"
          size="lg"
          onClick={() => window.location.reload()}
          sx={{ width: '100%', maxWidth: 480, mx: 'auto', display: 'flex' }}>
          تلاش مجدد
        </Button>
      </Box>
    </Box>
  );
}
